/**
 * Erkennt, ob dieselbe Papierverordnung schon einmal erfasst wurde.
 *
 * Hintergrund: Der Dedup in der Cloud Function
 * (`linkMedicalTransportEntryRidesToOrder`) greift nur bei **identischer**
 * `prescriptionScanId` und nur gegen Platzhalter-Verordnungen aus
 * Fahrer-Touren. Jeder neue Scan bekommt aber eine neue Scan-ID — dieselbe
 * Verordnung ein zweites Mal eingescannt ergab bisher lautlos einen zweiten
 * Datensatz. Beide zählen dann eigene Kontingente, und die Abrechnung
 * reicht Fahrten doppelt ein.
 *
 * Bewusst nur ein **Hinweis**, kein automatisches Zusammenführen: Ein Patient
 * kann am selben Tag zwei Verordnungen bekommen (verschiedene Behandlungen),
 * und ein falsch verschmolzener Abrechnungsfall ist ungleich mühsamer zu
 * reparieren als ein Klick.
 */

import { sameName, toDateKey } from './customerFromOrder';

const isSamePatient = (order, form) => {
  if (!sameName(order.patientName, form.patientName)) return false;

  const orderBirth = toDateKey(order.patientBirthDate);
  const formBirth = toDateKey(form.patientBirthDate);
  // Fehlt auf einer Seite das Geburtsdatum, entscheidet der Name allein —
  // lieber einmal zu viel nachfragen als eine Dublette übersehen.
  if (!orderBirth || !formBirth) return true;
  return orderBirth === formBirth;
};

/**
 * Warum gilt dieser Datensatz als möglicher Doppelgänger? Der Grund wird
 * angezeigt, damit der Disponent selbst entscheiden kann.
 */
const duplicateReason = (order, form) => {
  const prescriptionDate = toDateKey(form.prescriptionDate);
  if (prescriptionDate && toDateKey(order.prescriptionDate) === prescriptionDate) {
    return 'gleiches Ausstellungsdatum';
  }

  const validFrom = toDateKey(form.validFrom);
  const validUntil = toDateKey(form.validUntil);
  const orderFrom = toDateKey(order.validFrom);
  const orderUntil = toDateKey(order.validUntil);
  if (validFrom && validUntil && orderFrom && orderUntil
      && validFrom <= orderUntil && orderFrom <= validUntil) {
    return 'überschneidender Gültigkeitszeitraum';
  }

  return null;
};

/**
 * Liefert bestehende Verordnungen, die dieselbe Papierverordnung sein könnten.
 *
 * @param orders  bereits geladene Verordnungen (Mandanten-Scoping kommt aus
 *                `fetchMedicalTransportOrders`)
 * @param form    aktueller Formularstand
 * @param currentId  die gerade bearbeitete Verordnung, wird übersprungen
 */
export const findDuplicateOrders = (orders, form, currentId = null) => {
  if (!form?.patientName?.trim()) return [];

  return (orders || [])
    .filter(order => order.id !== currentId)
    .filter(order => order.archived !== true)
    .filter(order => isSamePatient(order, form))
    .map(order => ({ order, reason: duplicateReason(order, form) }))
    .filter(entry => entry.reason !== null);
};
